import { useEffect, useState } from 'react';

import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { SimpleSelect } from '@/components/ui/simple-select';
import DateRangeFilter, {
  DateRangeValue
} from '../date-range-filter/DateRangeFilter';
import { unableToCompleteRadioOptions } from '../more-filters/metadata/FilterOption';
import { accessTypeOptions } from '../../../../config/options.config';
import { useLazyGetOrderFilterMetadataQuery } from '../../../orders/services/orderApi';
import {
  SelectOption,
  restoreOrderFiltersFromQS
} from '../utils/advancedFilterUtils';

export interface OrderAdvancedFiltersProps {
  filter: string;
  selectedServices: SelectOption[];
  selectedOrderedBy: SelectOption[];
  appointmentDateRange: DateRangeValue | null;
  orderCompletedDateRange: DateRangeValue | null;
  accessType: string;
  unableToComplete: string;
  onAddressChange: (value: string) => void;
  onServicesChange: (value: SelectOption[]) => void;
  onOrderedByChange: (value: SelectOption[]) => void;
  onAppointmentDateChange: (value: DateRangeValue | null) => void;
  onOrderCompletedDateChange: (value: DateRangeValue | null) => void;
  onAccessTypeChange: (value: string) => void;
  onUnableToCompleteChange: (value: string) => void;
  /**
   * Externally supplied option lists (DV-2848 global orders view). When
   * present, the component renders these and skips its own
   * useLazyGetOrderFilterMetadataQuery fetch — that endpoint is scoped to
   * the caller's selected entity and is the wrong source for the
   * cross-account drawer, whose lists come from /internal/orders/filter-metadata
   * scoped per (window, statuses).
   */
  injectedOptions?: {
    services: SelectOption[];
    orderedBy: SelectOption[];
    loading: boolean;
  };
}

const OrderAdvancedFilters = ({
  filter,
  selectedServices, selectedOrderedBy,
  appointmentDateRange, orderCompletedDateRange,
  accessType, unableToComplete,
  onAddressChange, onServicesChange, onOrderedByChange,
  onAppointmentDateChange, onOrderCompletedDateChange,
  onAccessTypeChange, onUnableToCompleteChange,
  injectedOptions
}: OrderAdvancedFiltersProps) => {
  const isInjected = injectedOptions !== undefined;
  const [serviceOptions, setServiceOptions] = useState<SelectOption[]>([]);
  const [orderedByOptions, setOrderedByOptions] = useState<SelectOption[]>([]);
  const [pendingServiceSlugs, setPendingServiceSlugs] = useState<string[]>([]);
  const [triggerFilterMetadata, { isFetching: isLoadingFilterOptions }] =
    useLazyGetOrderFilterMetadataQuery();

  const effectiveServiceOptions = injectedOptions?.services ?? serviceOptions;
  const effectiveOrderedByOptions = injectedOptions?.orderedBy ?? orderedByOptions;
  const effectiveLoading = injectedOptions?.loading ?? isLoadingFilterOptions;

  useEffect(() => {
    if (isInjected) return;
    triggerFilterMetadata()
      .unwrap()
      .then((data) => {
        if (data?.services) {
          setServiceOptions(
            data.services.map((s) => ({
              value: s.shoppingCartSlug, label: s.name
            }))
          );
        }
        if (data?.orderedBy) {
          setOrderedByOptions(
            data.orderedBy.map((name) => ({ value: name, label: name }))
          );
        }
      })
      .catch(() => { /* options remain empty */ });
    // Mount-only fetch of filter options; the trigger is a stable RTK Query
    // helper.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pendingServiceSlugs.length > 0 && effectiveServiceOptions.length > 0) {
      const resolved = pendingServiceSlugs
        .map((slug) => effectiveServiceOptions.find((o) => o.value === slug))
        .filter(Boolean) as SelectOption[];
      if (resolved.length > 0) onServicesChange(resolved);
      setPendingServiceSlugs([]);
    }
  }, [pendingServiceSlugs, effectiveServiceOptions]);

  useEffect(() => {
    if (!filter) {
      onAddressChange('');
      onServicesChange([]);
      onOrderedByChange([]);
      onAppointmentDateChange(null);
      onOrderCompletedDateChange(null);
      onUnableToCompleteChange('');
      onAccessTypeChange('');
      setPendingServiceSlugs([]);
      return;
    }
    const r = restoreOrderFiltersFromQS(filter);
    if (r.simple.address) onAddressChange(r.simple.address);
    if (r.simple.accessType) onAccessTypeChange(r.simple.accessType);
    if (r.unableToComplete) onUnableToCompleteChange(r.unableToComplete);
    if (r.serviceSlugs.length > 0) {
      if (effectiveServiceOptions.length > 0) {
        const resolved = r.serviceSlugs
          .map((slug) => effectiveServiceOptions.find((o) => o.value === slug))
          .filter(Boolean) as SelectOption[];
        onServicesChange(resolved);
      } else {
        setPendingServiceSlugs(r.serviceSlugs);
      }
    } else { onServicesChange([]); }
    onOrderedByChange(r.orderedByValues.length > 0 ? r.orderedByValues : []);
    onAppointmentDateChange(r.appointmentDateRange);
    onOrderCompletedDateChange(r.orderCompletedDateRange);
  }, [filter]);

  const handleFilterChange = (field: string, value: string) => {
    if (field === 'accessType') onAccessTypeChange(value);
  };

  return (
    <div className="space-y-0">
      <div className="border-b border-border py-6 first:pt-0 last:border-b-0 last:pb-0">
        <Label className="block mb-3">Services Ordered</Label>
        <MultiSelect
          options={effectiveServiceOptions}
          value={selectedServices}
          onChange={(sel) => onServicesChange(sel as SelectOption[])}
          // Resting label for the CLOSED trigger — deliberately not the
          // search prompt, which belongs to the input inside the menu.
          // Passing the same string to both read as a duplicated field.
          placeholder="Select Services..."
          searchPlaceholder="Search Services..."
          emptyMessage="No services found"
          loading={effectiveLoading}
          loadingMessage="Loading services…"
          aria-label="Services Ordered"
        />
      </div>

      <div className="border-b border-border py-6 last:border-b-0 last:pb-0">
        <Label className="block mb-3">Unable to Complete</Label>
        <RadioGroup
          value={unableToComplete} onValueChange={onUnableToCompleteChange}
          name="unableToComplete" className="grid gap-2"
        >
          {unableToCompleteRadioOptions.map((opt) => (
            <div key={opt.value} className="flex items-center gap-2">
              <RadioGroupItem value={opt.value} id={opt.id ?? opt.value} />
              <Label htmlFor={opt.id ?? opt.value} className="font-normal text-foreground">{opt.label}</Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="border-b border-border py-6 last:border-b-0 last:pb-0">
        <Label className="block mb-3">Ordered By</Label>
        <MultiSelect
          options={effectiveOrderedByOptions}
          value={selectedOrderedBy}
          onChange={(sel) => onOrderedByChange(sel as SelectOption[])}
          placeholder="Select Name..."
          searchPlaceholder="Search by Name..."
          emptyMessage="No results found"
          loading={effectiveLoading}
          loadingMessage="Loading names…"
          aria-label="Ordered By"
        />
      </div>

      <div className="border-b border-border py-6 last:border-b-0 last:pb-0">
        <DateRangeFilter
          label="Order Completed Date" value={orderCompletedDateRange}
          onChange={onOrderCompletedDateChange} inline
        />
      </div>

      <div className="border-b border-border py-6 last:border-b-0 last:pb-0">
        <DateRangeFilter
          label="Appointment Date" value={appointmentDateRange}
          onChange={onAppointmentDateChange} inline
        />
      </div>

      <div className="py-6 last:pb-0">
        <Label htmlFor="accessType" className="block mb-3">Access Type</Label>
        <SimpleSelect
          options={accessTypeOptions} name="accessType" md="12"
          controlId="accessType" ariaDescribedBy="accessType-select"
          placeholder="Select Access Type"
          value={accessType ? { value: accessType, label: accessType } : ''}
          setFieldValue={handleFilterChange} field="accessType"
        />
      </div>
    </div>
  );
};

export default OrderAdvancedFilters;
